<script lang="ts" setup>
import { computed, nextTick, ref } from "vue";
import {
  type FormDefinition,
  type FormElementBase,
  FormElementType,
} from "~/api";

import { FormPage } from "../../composables/useFormResponse";

const props = defineProps<{
  form: FormDefinition;
  formPages?: FormPage[];
}>();

const emit = defineEmits<{
  (e: "handleSelectMenuItem", value: FormElementBase): void;
  (e: "switchPage", value: number): void;
}>();

/** Local copy of form */
const formLocal = ref<FormDefinition>(props.form);

const tableOfContentHeadings = computed<TOCSection[]>(() => {
  const sections: TOCSection[] = formLocal.value.components
    .filter((component) => component.type === FormElementType.SECTION)
    .map((section) => {
      const sectionHeaders = formLocal.value.components
        .filter(
          (header) =>
            header.parentGuid === section.guid &&
            header.type === FormElementType.HEADING,
        )
        .map((header) => ({
          id: header.questionCode,
          name: `${header.label || "Unnamed Page"}`,
        }));

      return {
        id: section.questionCode,
        name: section.label || "Section",
        headers: sectionHeaders,
      };
    });
  return sections;
});

interface TOCPage {
  id: string;
  name: string;
}

interface TOCSection {
  id: string;
  name: string;
  headers: TOCPage[];
}

const handleClickTOCItem = (id: string) => {
  // switch to the correct form page
  if (props.formPages) {
    const componentClicked = props.form.components.find(
      (c) => c.questionCode === id,
    );
    if (componentClicked) {
      const componentClickedPageNumber =
        props.formPages.findIndex(
          (p) => p.components.some((c) => c.questionCode === id),
          // page numbers are not zero-indexed
        ) + 1;
      emit("switchPage", componentClickedPageNumber);
    }
  }

  // scroll to the element clicked on
  nextTick(() => {
    const element = document.getElementsByClassName(id);
    if (element && element.length > 0) {
      element[0].scrollIntoView({ behavior: "smooth" });
    }
  });
};
</script>

<template>
  <div class="toc">
    <div class="tocTitle">Table of Contents</div>
    <div class="sections">
      <div
        v-for="section in tableOfContentHeadings"
        :key="section.id"
        :index="section.id.toString()"
        class="section"
      >
        <div class="sectionName" @click="handleClickTOCItem(section.id)">
          {{ section.name || "(Section)" }}
        </div>
        <div v-if="section.headers.length" class="headers">
          <div
            v-for="header in section.headers"
            :key="header.id"
            class="header"
            :index="`${section.id}-${header.id}`"
            @click="handleClickTOCItem(header.id)"
          >
            {{ header.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.toc {
  // may not be effective in the popover
  overflow: auto;
  height: 100%;

  .tocTitle {
    font-size: 1.2em;
    margin-bottom: 0.5em;
    border-bottom: 1px solid #ebeef5;
  }

  .headers {
    margin: 0.5em 0 0 1em;
  }

  .header {
    font-size: 1.1em;
    height: auto;
  }
  .sectionName,
  .header {
    cursor: pointer;
    padding: 0.1em 0.5em 0.2em 0.5em;

    &:hover {
      background-color: #f5f7fa;
    }
  }
}
</style>
